export type ResultChannel = "ALL" | "AUDIENCE" | "EVALUATION";
export type RoundAction = "READY" | "OPEN" | "PAUSE" | "RESUME" | "CLOSE" | "SEAL";
export type LiveTeam = { entryId: string; partyId?: string; displayName: string; score: string | null; rank: number | null; contributors: number | null };
export type RoundAnalytics = {
  roundId: string; channel: ResultChannel; state: string; version: number; serverTimeEpochMillis: number;
  schedule?: { mode?: string; closesAtEpochMillis?: number } | null;
  pause?: { pausedAtEpochMillis: number; remainingMillis?: number | null } | null;
  preliminary: true; statisticsStatus: string; statistics: { participantCount: number; ballotCount: number } | null;
  eligibleTeamCount: number | null; resultsStatus: string; items: LiveTeam[]; refreshAfterMillis?: number;
};
export type SeasonAnalytics = {
  unitId: string; statisticsStatus: string; participantCount: number | null; ballotCount: number | null;
  roundCount: number; sealedRoundCount: number;
  rounds: { roundId: string; title: string; state: string; participantCount: number | null; statisticsStatus: string }[];
  serverTimeEpochMillis: number; refreshAfterMillis?: number;
};
export type VoterRow = {
  rowId: string; id: string; name: string; roundId: string; roundName: string; entryIds: string[]; entryNames: string[];
  submittedAtEpochMillis: number; method: string; channel: string; roleCode: string; countedInResults: boolean;
};
export type VoterPage = { status: string; items: VoterRow[]; totalCount: number | null; nextCursor: string | null; dataCompleteness?: string; historicalIdentitiesErased?: boolean;
  entryOptions?: { entryId: string; displayName: string }[] };

export function wholeCount(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export function analyticsPath(path: string, query: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(key, String(value));
  return path + "?" + params;
}

export function countAxis(values: (number | null)[]): { maximum: number; ticks: number[] } {
  const maximum = Math.max(0, ...values.map(value => wholeCount(value) ?? 0));
  if (maximum <= 1) return { maximum: 1, ticks: [0, 1] };
  const rawStep = maximum / 4;
  const power = 10 ** Math.floor(Math.log10(rawStep));
  const step = Math.max(1, [1, 2, 5, 10].find(value => value * power >= rawStep)! * power);
  const top = Math.ceil(maximum / step) * step;
  return { maximum: top, ticks: Array.from({ length: Math.round(top / step) + 1 }, (_, index) => index * step) };
}

export function remainingRoundSeconds(data: RoundAnalytics | undefined, elapsedMillis: number): number | null {
  if (!data || data.schedule?.mode !== "SCHEDULED") return null;
  if (data.state === "PAUSED") return wholeCount(data.pause?.remainingMillis) === null ? null : Math.ceil(data.pause!.remainingMillis! / 1000);
  if (data.state !== "OPEN" || !Number.isFinite(data.schedule.closesAtEpochMillis) || !Number.isFinite(data.serverTimeEpochMillis)) return null;
  return Math.max(0, Math.ceil((data.schedule.closesAtEpochMillis! - data.serverTimeEpochMillis - Math.max(0, elapsedMillis)) / 1000));
}

export function csvCell(value: unknown): string {
  let text = value == null ? "" : String(value);
  // Quoting alone does not stop spreadsheet formula execution.
  if (/^[\s\uFEFF]*[=+@\-]/u.test(text) || /^[\t\r\n]/u.test(text)) text = "'" + text;
  return '"' + text.replaceAll('"', '""') + '"';
}

export function voterCsv(rows: VoterRow[], headers: string[], formatTime: (time: number) => string): string {
  return "\uFEFF" + [headers, ...rows.map(row => [row.name, row.roundName, row.entryNames.join(" | "), formatTime(row.submittedAtEpochMillis), row.method, row.channel, row.countedInResults ? "1" : "0"])]
    .map(cells => cells.map(csvCell).join(",")).join("\r\n");
}
